import { Badge } from '@/shared/components/ui/Badge';
import type { Manufacturer } from '@/shared/types/domain.types';

interface ManufacturerRulesCardProps {
  manufacturer: Manufacturer;
  selectedFrameCount: number;
  totalFrameCount: number;
}

export function ManufacturerRulesCard({
  manufacturer,
  selectedFrameCount,
}: ManufacturerRulesCardProps) {
  const hasMinOrderQty = manufacturer.min_order_qty !== null && manufacturer.min_order_qty > 0;
  const hasContact =
    manufacturer.contact_phone ||
    manufacturer.contact_email ||
    manufacturer.rep_name ||
    manufacturer.rep_phone;

  // Check if selected frames are below minimum (only when frames are selected)
  const belowMinOrder = hasMinOrderQty && selectedFrameCount > 0 && selectedFrameCount < manufacturer.min_order_qty!;
  const framesNeeded = hasMinOrderQty && selectedFrameCount > 0
    ? Math.max(0, manufacturer.min_order_qty! - selectedFrameCount)
    : 0;

  if (!hasMinOrderQty && !hasContact) {
    return null; // Don't show card if no rules or contact exist
  }

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
      <div className="flex flex-wrap items-start gap-4">
        {/* Ordering Rules */}
        {hasMinOrderQty && (
          <div className="flex-1 min-w-[200px]">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Ordering Rules</h4>
            <div className="space-y-1 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-gray-600">Min Order:</span>
                <span className="font-medium text-gray-900">
                  {manufacturer.min_order_qty} frames
                </span>
                {belowMinOrder && (
                  <Badge variant="warning" size="sm">
                    Below minimum
                  </Badge>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Contact Info */}
        {hasContact && (
          <div className="flex-1 min-w-[200px]">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Contact Info</h4>
            <div className="space-y-1 text-sm">
              {manufacturer.rep_name && (
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">Rep:</span>
                  <span className="font-medium text-gray-900">{manufacturer.rep_name}</span>
                </div>
              )}
              {manufacturer.rep_phone && (
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">Rep Phone:</span>
                  <a
                    href={`tel:${manufacturer.rep_phone}`}
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    {manufacturer.rep_phone}
                  </a>
                </div>
              )}
              {manufacturer.contact_phone && (
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">Main Phone:</span>
                  <a
                    href={`tel:${manufacturer.contact_phone}`}
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    {manufacturer.contact_phone}
                  </a>
                </div>
              )}
              {manufacturer.contact_email && (
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">Email:</span>
                  <a
                    href={`mailto:${manufacturer.contact_email}`}
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    {manufacturer.contact_email}
                  </a>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Selected Count */}
        {selectedFrameCount > 0 && (
          <div className="flex-shrink-0">
            <div className="bg-blue-100 text-blue-800 px-4 py-2 rounded-md text-center">
              <div className="text-2xl font-bold">{selectedFrameCount}</div>
              <div className="text-xs uppercase tracking-wide">Selected</div>
            </div>
          </div>
        )}
      </div>

      {/* Warning Message */}
      {belowMinOrder && (
        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <p className="text-sm text-yellow-800">
            ⚠️ Warning: You have selected {selectedFrameCount} frame{selectedFrameCount !== 1 ? 's' : ''}, which is below the minimum order quantity of {manufacturer.min_order_qty}.
            {framesNeeded > 0 && (
              <span className="font-semibold">
                {' '}
                You need {framesNeeded} more frame{framesNeeded !== 1 ? 's' : ''} to reach the minimum.
              </span>
            )}
            {' '}You can still proceed, but you may incur additional fees.
          </p>
        </div>
      )}

      {/* Notes */}
      {manufacturer.notes && (
        <div className="mt-3 p-3 bg-gray-100 rounded-md">
          <p className="text-sm text-gray-700">
            <span className="font-semibold">Note:</span> {manufacturer.notes}
          </p>
        </div>
      )}
    </div>
  );
}
